import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { QuoteChange, QuoteChangePercent, getTimeLabels, isExtendedHours, regularSessionQuote } from '@benzinga/quotes-ui';
import { AFTER_MARKET, NO_TRADING, REGULAR, PRE_MARKET } from '@benzinga/quotes-manager/constants';
import type { QuoteSessionType, DelayedQuote } from '@benzinga/quotes-manager';
import type { Quote } from '@benzinga/quotes-v3-manager';
import { formatPriceWithCurrency } from '@benzinga/utils/numberUtils';
import { useIsMounted } from '@benzinga/hooks';
import { Icon } from '@benzinga/core-ui';
import type { BaseQuoteProfile } from '../../utils/quote/types';
interface Props {
  isCrypto?: boolean;
  isRealTime?: boolean;
  profile: BaseQuoteProfile;
  quotes: Quote;
}
const quoteHeaderPriceBoxDefaultClassName = 'quote-header-price-box flex items-center justify-between gap-x-4 p-[14.5px] rounded-md w-full min-h-[80px]';
const getWidthClassByPrice = (price: number | null | undefined, isMain = false): string => {
  const baseWidths = {
    390: isMain ? 'md:w-[390px]' : 'md:w-[415px]',
    420: isMain ? 'md:w-[420px]' : 'md:w-[430px]',
    430: isMain ? 'md:w-[410px]' : 'md:w-[430px]',
    450: isMain ? 'md:w-[430px]' : 'md:w-[450px]',
    480: isMain ? 'md:w-[460px]' : 'md:w-[480px]',
    490: isMain ? 'md:w-[470px]' : 'md:w-[490px]',
    540: isMain ? 'md:w-[520px]' : 'md:w-[540px]'
  };
  const safePrice = price ?? 0;
  const formattedLength = safePrice > 0 && safePrice < 1 ? formatPriceWithCurrency(safePrice, 'USD').length : 0;
  switch (true) {
    case safePrice > 0 && safePrice < 1 && formattedLength <= 7:
      return baseWidths[430];
    case safePrice > 0 && safePrice < 1 && formattedLength <= 9:
      return baseWidths[490];
    case safePrice > 0 && safePrice < 1 && formattedLength > 9:
      return baseWidths[540];
    case safePrice < 100:
      return baseWidths[390];
    case safePrice < 1000:
      return baseWidths[420];
    case safePrice < 10000:
      return baseWidths[450];
    case safePrice < 100000:
      return baseWidths[480];
    default:
      return baseWidths[540];
  }
};
const generatedColoredBoxClassName = (change: number | null, price: number | null | undefined) => {
  const mainWidth = getWidthClassByPrice(price, true);
  return classNames(quoteHeaderPriceBoxDefaultClassName, mainWidth, {
    'bg-bzgray-300 border border-bzgray-500': !change,
    'bg-bzgreen-400/20 border border-green-700/20': change && change > 0,
    'bg-bzred-500/10 border border-red-500/20': change && change < 0
  });
};
const ET = 'America/New_York';
const SECONDARY_PRICE_BOX_HIDE_START_MINUTES = 16 * 60;
const SECONDARY_PRICE_BOX_HIDE_END_MINUTES = 16 * 60 + 15;
export const shouldHideSecondaryPriceBox = (profile: BaseQuoteProfile): boolean => {
  const session = profile?.schedule?.type;
  const isAfterHoursSession = session === AFTER_MARKET || session === NO_TRADING;
  if (!isAfterHoursSession) {
    return false;
  }
  const nowEt = DateTime.now().setZone(ET);
  const nowEtMinutes = nowEt.hour * 60 + nowEt.minute;
  return nowEtMinutes >= SECONDARY_PRICE_BOX_HIDE_START_MINUTES && nowEtMinutes < SECONDARY_PRICE_BOX_HIDE_END_MINUTES;
};
export const QuoteHeaderPriceBox: React.FC<Props> = t0 => {
  "use memo";

  const $ = _c(23);
  const {
    isCrypto,
    isRealTime,
    profile,
    quotes
  } = t0;
  const {
    t
  } = useTranslation("quote");
  const [isMountedDelayed, setIsMountedDelayed] = useState(false);
  const mounted = useIsMounted();
  let t1;
  let t2;
  if ($[0] !== mounted) {
    t1 = () => {
      if (!mounted) {
        return;
      }
      const timer = setTimeout(() => {
        setIsMountedDelayed(true);
      }, 2000);
      return () => clearTimeout(timer);
    };
    t2 = [mounted];
    $[0] = mounted;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const priceDataNotAvailable = !profile?.richQuoteData?.lastTradePrice && !profile?.richQuoteData?.close;
  const showDelayedMessageSpacer = !profile.isPrivate;
  const t3 = !priceDataNotAvailable && showDelayedMessageSpacer;
  let t4;
  if ($[3] !== priceDataNotAvailable || $[4] !== t3) {
    t4 = classNames("w-full flex flex-col items-center justify-center", {
      "h-8": priceDataNotAvailable,
      "min-h-[110px]": t3
    });
    $[3] = priceDataNotAvailable;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isCrypto || $[7] !== isRealTime || $[8] !== priceDataNotAvailable || $[9] !== profile || $[10] !== quotes) {
    t5 = <div className="flex flex-wrap lg:flex-nowrap gap-2 w-full">{priceDataNotAvailable ? <PriceDataUnavailable isPrivate={profile.isPrivate} /> : isCrypto ? <CryptoPriceBox isRealTime={isRealTime} profile={profile} quotes={quotes} /> : profile.isPrivate ? <PrivatePriceBox profile={profile} /> : <><MainPriceBox isRealTime={isRealTime} profile={profile} quotes={quotes} />{!isCrypto && !shouldHideSecondaryPriceBox(profile) && <SecondaryPriceBox profile={profile} quotes={quotes} />}</>}</div>;
    $[6] = isCrypto;
    $[7] = isRealTime;
    $[8] = priceDataNotAvailable;
    $[9] = profile;
    $[10] = quotes;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== isMountedDelayed || $[13] !== isRealTime || $[14] !== priceDataNotAvailable || $[15] !== profile.wiim || $[16] !== showDelayedMessageSpacer || $[17] !== t) {
    t6 = showDelayedMessageSpacer && <div className={classNames("w-full whitespace-nowrap text-xs mt-1 text-gray-600 h-[16px]", {
      "mb-2": !profile?.wiim
    })}>{!priceDataNotAvailable && !isRealTime && isMountedDelayed && <i>{t("Quote.Market.minutes-delayed", {
          minutes: 15
        })}</i>}</div>;
    $[12] = isMountedDelayed;
    $[13] = isRealTime;
    $[14] = priceDataNotAvailable;
    $[15] = profile.wiim;
    $[16] = showDelayedMessageSpacer;
    $[17] = t;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t4 || $[20] !== t5 || $[21] !== t6) {
    t7 = <div className={t4}>{t5}{t6}</div>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
};
const MainPriceBox: React.FC<Props> = t0 => {
  "use memo";

  const $ = _c(27);
  const {
    isRealTime,
    profile,
    quotes
  } = t0;
  const {
    t
  } = useTranslation("quote");
  let t1;
  let values;
  if ($[0] !== isRealTime || $[1] !== profile || $[2] !== quotes || $[3] !== t) {
    values = getMainPriceBoxValues(profile, quotes, isRealTime, t);
    t1 = generatedColoredBoxClassName(values.change, values.price);
    $[0] = isRealTime;
    $[1] = profile;
    $[2] = quotes;
    $[3] = t;
    $[4] = t1;
    $[5] = values;
  } else {
    t1 = $[4];
    values = $[5];
  }
  const className = t1;
  let t2;
  if ($[6] !== values.price) {
    t2 = typeof values.price === "number" && <PriceText price={values.price} />;
    $[6] = values.price;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  let t4;
  if ($[8] !== values.change) {
    t3 = <QuoteChangeIcon change={values.change} />;
    t4 = typeof values.change === "number" && <QuoteChange absoluteChange={values.change} positiveColor="text-green-600" />;
    $[8] = values.change;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  let t5;
  if ($[11] !== values.change || $[12] !== values.changePercent) {
    t5 = typeof values.changePercent === "number" && typeof values.change === "number" && <QuoteHeaderChangePercent change={values.change} changePercent={values.changePercent} />;
    $[11] = values.change;
    $[12] = values.changePercent;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
    t6 = <div className="quote-change-data flex items-center gap-2 text-bzgray-900 text-base min-h-[26px]">{t3}{t4}{t5}</div>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== values.timeLabel) {
    t7 = <TimeLabel text={values.timeLabel} />;
    $[18] = values.timeLabel;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t6 || $[21] !== t7) {
    t8 = <div className="ml-auto min-w-[200px] lg:min-w-[unset]">{t6}{t7}</div>;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== className || $[24] !== t2 || $[25] !== t8) {
    t9 = <div className={className}>{t2}{t8}</div>;
    $[23] = className;
    $[24] = t2;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
};
const PrivatePriceBox: React.FC<{
  profile: BaseQuoteProfile;
}> = ({
  profile
}) => {
  const price = profile.richQuoteData?.lastTradePrice ?? profile.richQuoteData?.close ?? null;
  const previousClosePrice = profile.richQuoteData?.previousClosePrice ?? null;
  const change = typeof price === 'number' && typeof previousClosePrice === 'number' ? price - previousClosePrice : null;
  const changePercent = typeof change === 'number' && previousClosePrice ? change / previousClosePrice * 100 : null;
  const className = generatedColoredBoxClassName(change, price);
  return <div className={className}>
      {typeof price === 'number' && <PriceText price={price} />}
      <div className="ml-auto min-w-[200px] lg:min-w-[unset]">
        <div className="quote-change-data flex items-center gap-2 text-bzgray-900 text-base min-h-[26px]">
          <QuoteChangeIcon change={change} />
          {typeof change === 'number' && <QuoteChange absoluteChange={change} positiveColor="text-green-600" />}
          {typeof changePercent === 'number' && typeof change === 'number' && <QuoteHeaderChangePercent change={change} changePercent={changePercent} />}
        </div>
        <TimeLabel text="Nasdaq Private Market" />
      </div>
    </div>;
};
const SecondaryPriceBox: React.FC<Props> = t0 => {
  "use memo";

  const $ = _c(27);
  const {
    profile,
    quotes
  } = t0;
  const {
    t
  } = useTranslation("quote");
  let t1;
  let t2;
  let values;
  if ($[0] !== profile || $[1] !== quotes || $[2] !== t) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      values = getSecondaryPriceBoxValues(profile, quotes, t);
      if (!values.price) {
        t2 = null;
        break bb0;
      }
      t1 = getWidthClassByPrice(values.price, false);
    }
    $[0] = profile;
    $[1] = quotes;
    $[2] = t;
    $[3] = t1;
    $[4] = t2;
    $[5] = values;
  } else {
    t1 = $[3];
    t2 = $[4];
    values = $[5];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const widthClass = t1;
  const t3 = `${quoteHeaderPriceBoxDefaultClassName} ${widthClass} bg-bzgray-200 text-bzgray-900 border border-bzgray-400`;
  let t4;
  if ($[6] !== values.price) {
    t4 = typeof values.price === "number" && <PriceText price={values.price} />;
    $[6] = values.price;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  let t6;
  if ($[8] !== values.change) {
    t5 = <QuoteChangeIcon change={values.change} />;
    t6 = typeof values.change === "number" && <QuoteChange absoluteChange={values.change} positiveColor="text-green-600" />;
    $[8] = values.change;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[11] !== values.change || $[12] !== values.changePercent) {
    t7 = typeof values.changePercent === "number" && typeof values.change === "number" && <QuoteHeaderChangePercent change={values.change} changePercent={values.changePercent} />;
    $[11] = values.change;
    $[12] = values.changePercent;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t6 || $[16] !== t7) {
    t8 = <div className="quote-change-data flex items-center gap-2 text-base min-h-[26px]">{t5}{t6}{t7}</div>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== values.timeLabel) {
    t9 = <TimeLabel text={values.timeLabel} />;
    $[18] = values.timeLabel;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = <div className="ml-auto min-w-[200px] lg:min-w-[unset]">{t8}{t9}</div>;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t3 || $[25] !== t4) {
    t11 = <div className={t3}>{t4}{t10}</div>;
    $[23] = t10;
    $[24] = t3;
    $[25] = t4;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
};
const CryptoPriceBox: React.FC<Props> = t0 => {
  "use memo";

  const $ = _c(30);
  const {
    isRealTime,
    profile,
    quotes
  } = t0;
  const {
    t
  } = useTranslation("quote");
  const profileQuote = profile?.richQuoteData;
  const price = isRealTime ? (quotes as Quote)?.price ?? profileQuote?.lastTradePrice : profileQuote?.lastTradePrice;
  const change = isRealTime ? quotes?.change ?? profileQuote?.change ?? null : profileQuote?.change ?? null;
  const changePercent = isRealTime ? quotes?.changePercent ?? profileQuote?.changePercent : profileQuote?.changePercent;
  const lastTradeTime = isRealTime ? quotes?.time || quotes?.lastTradeTime || profileQuote?.lastTradeTime : profileQuote?.lastTradeTime;
  let t1;
  if ($[0] !== lastTradeTime) {
    t1 = lastTradeTime ? DateTime.fromMillis(lastTradeTime).setZone("UTC").toFormat("h:mm a ZZZZ") : null;
    $[0] = lastTradeTime;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const updateTime = t1;
  let t2;
  if ($[2] !== lastTradeTime || $[3] !== t || $[4] !== updateTime) {
    t2 = lastTradeTime ? `${t("Quote.Price.last-update")}: ${updateTime}` : "-";
    $[2] = lastTradeTime;
    $[3] = t;
    $[4] = updateTime;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const timeLabel = t2;
  const t3 = change as number | null;
  let t4;
  if ($[6] !== price || $[7] !== t3) {
    t4 = generatedColoredBoxClassName(t3, price);
    $[6] = price;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const className = t4;
  const t5 = `${className} whitespace-nowrap`;
  let t6;
  if ($[9] !== price) {
    t6 = typeof price === "number" && <PriceText price={price} />;
    $[9] = price;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  let t8;
  if ($[11] !== change) {
    t7 = <QuoteChangeIcon change={change} />;
    t8 = typeof change === "number" && <QuoteChange absoluteChange={change} positiveColor="text-green-600" />;
    $[11] = change;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  let t9;
  if ($[14] !== change || $[15] !== changePercent) {
    t9 = typeof changePercent === "number" && <QuoteHeaderChangePercent change={change} changePercent={changePercent} />;
    $[14] = change;
    $[15] = changePercent;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = <div className="quote-change-data flex items-center gap-2 text-bzred-500 text-base min-h-[26px]">{t7}{t8}{t9}</div>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== timeLabel) {
    t11 = <TimeLabel text={timeLabel} />;
    $[21] = timeLabel;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11) {
    t12 = <div>{t10}{t11}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t5 || $[28] !== t6) {
    t13 = <div className={t5}>{t6}{t12}</div>;
    $[26] = t12;
    $[27] = t5;
    $[28] = t6;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
};
const TimeLabel = ({
  text
}: {
  text: string | null;
}) => {
  // Real-time timestamp: the SSR snapshot and live client value legitimately differ.
  // Suppress the hydration warning for this text node only — no rendering logic changes.
  return <div className="text-bzgray-600 text-xs font-semibold uppercase mt-1 whitespace-nowrap" suppressHydrationWarning>
      {text}
    </div>;
};
const PriceDataUnavailable: React.FC<{
  isPrivate?: boolean;
}> = ({
  isPrivate
}) => {
  return <div className="flex items-center gap-1 text-bzred-500 text-sm font-semibold h-8">
      <Icon className="h-5 w-5 fill-current mt-2" icon={faExclamationTriangle} />
      <span>{isPrivate ? 'Private market price is not available' : 'Price data is not available'}</span>
    </div>;
};
const PriceText = ({
  price
}: {
  price: number;
}) => {
  // Real-time price: the SSR snapshot and the live client value legitimately differ.
  // Suppress the hydration warning for this text node only (client value wins) — the
  // rendering logic and conditional rendering are unchanged from the original.
  return <div className="text-3xl md:text-5xl text-bzgray-900 leading-none" suppressHydrationWarning>
      {formatPriceWithCurrency(price, 'USD')}
    </div>;
};
const QuoteHeaderChangePercent = ({
  change,
  changePercent
}: {
  change: number | null;
  changePercent: number;
}) => {
  return <span className={classNames('px-1 py-[1px] rounded', {
    'bg-bzgray-100': !change,
    'bg-bzgreen-500/20': change && change > 0,
    'bg-bzred-500/10': change && change < 0
  })}>
      <QuoteChangePercent percentageChange={changePercent} positiveColor="text-green-600" />
    </span>;
};
const QuoteChangeIcon = ({
  change
}: {
  change: number | null;
}) => {
  if (!change) return null;
  return <div className={classNames('flex items-center justify-center p-3 rounded w-5 h-5', {
    'bg-bzgray-100': !change,
    'bg-bzgreen-500/20': change && change > 0,
    'bg-bzred-500/10': change && change < 0,
    'text-bzred-500': change && change < 0,
    'text-green-600': change && change > 0
  })}>
      <span>
        <svg className="feather feather-arrow-up-right" fill="none" height="20" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" viewBox="0 0 24 24" width="20" xmlns="http://www.w3.org/2000/svg">
          {change > 0 ? <>
              <path d="M7 17L17 7"></path>
              <path d="M7 7L17 7 17 17"></path>
            </> : <>
              <path d="M7 7L17 17"></path>
              <path d="M17 7L17 17 7 17"></path>
            </>}
        </svg>
      </span>
    </div>;
};
export const getMainPriceBoxValues = (profile: BaseQuoteProfile, quotes: Quote, isRealTime?: boolean, translateFn?: (key: string, options?: {
  ns?: string;
}) => string): {
  change: number | null;
  changePercent: number | null;
  price: number | null | undefined;
  timeLabel: string;
} => {
  const close = profile?.richQuoteData?.close;
  const previousClosePrice = profile?.richQuoteData?.previousClosePrice;
  const session = profile?.schedule?.type;
  const isRegular = session === REGULAR;
  const isNoTrading = session === NO_TRADING;
  const isPremarket = session === PRE_MARKET;
  let price: number | null | undefined = null;
  let change: number | null = null;
  let changePercent: number | null = null;
  let timeLabel = '-';
  if (isRegular) {
    price = isRealTime ? (quotes as Quote)?.price ?? quotes?.askPrice : profile?.richQuoteData?.lastTradePrice ?? profile?.richQuoteData?.close;
    change = price && previousClosePrice ? price - previousClosePrice : quotes?.change ?? null;
    changePercent = change && previousClosePrice ? change / previousClosePrice * 100 : quotes?.changePercent ?? null;
    const quoteData = isRealTime ? quotes as DelayedQuote : profile?.richQuoteData;
    timeLabel = quoteData ? getTimeLabels(quoteData, session, {
      isRealTime,
      longDate: true
    }, translateFn).timestampClose : '-';
  } else if (close && previousClosePrice) {
    price = close;
    change = price - previousClosePrice;
    changePercent = change / previousClosePrice * 100;
  } else if (isNoTrading && previousClosePrice) {
    price = previousClosePrice;
    change = quotes?.change ?? null;
    changePercent = quotes?.changePercent ?? null;
  } else if (isPremarket && previousClosePrice) {
    price = previousClosePrice;
    change = quotes?.change ?? null;
    changePercent = quotes?.changePercent ?? null;
  } else if (!close) {
    const [newPrice, newChange, newChangePercent] = regularSessionQuote(profile?.richQuoteData?.close || quotes?.close, profile?.richQuoteData?.previousClose || quotes?.previousClose, profile?.richQuoteData?.lastTradePrice);
    price = newPrice;
    change = newChange ?? null;
    changePercent = newChangePercent ?? null;
  }
  if (price == null) {
    price = close ?? profile?.richQuoteData?.lastTradePrice ?? null;
    if (change == null && typeof price === 'number' && typeof previousClosePrice === 'number') {
      change = price - previousClosePrice;
      changePercent = previousClosePrice !== 0 ? change / previousClosePrice * 100 : changePercent;
    }
  }
  if (!(isRegular && isRealTime)) {
    timeLabel = profile?.richQuoteData ? getTimeLabels(profile.richQuoteData, session, {
      longDate: true
    }, translateFn).timestampClose : '-';
  }
  return {
    change,
    changePercent,
    price,
    timeLabel
  };
};
export const getSecondaryPriceBoxValues = (profile: BaseQuoteProfile, quotes?: Quote, translateFn?: (key: string, options?: {
  ns?: string;
}) => string): {
  change: number | null;
  changePercent: number | null;
  price: number | null | undefined;
  timeLabel: string | null;
} => {
  let price: number | null | undefined = null;
  let change: number | null = null;
  let changePercent: number | null = null;
  let timeLabel = '-';
  const session = profile?.schedule?.type as QuoteSessionType;
  const isRegular = session === REGULAR;
  const isPremarket = session === PRE_MARKET;
  if (isRegular) {
    return {
      change: null,
      changePercent: null,
      price: null,
      timeLabel: null
    };
  }
  const closePrice = profile?.richQuoteData?.close;
  const ethPrice = profile?.richQuoteData?.ethPrice;
  if (isExtendedHours(session) && !isPremarket && ethPrice === undefined) {
    return {
      change: null,
      changePercent: null,
      price: null,
      timeLabel: null
    };
  }
  if (isPremarket && quotes) {
    const previousClosePrice = profile?.richQuoteData?.previousClosePrice;
    price = (quotes as Quote)?.price ?? quotes?.askPrice ?? profile?.richQuoteData?.lastTradePrice;
    change = quotes?.change ?? (price && previousClosePrice ? price - previousClosePrice : null);
    changePercent = quotes?.changePercent ?? (change && previousClosePrice ? change / previousClosePrice * 100 : null);
    timeLabel = profile?.richQuoteData ? getTimeLabels(profile.richQuoteData, session, undefined, translateFn).timestampEth : '-';
  } else if (isExtendedHours(session) && !isPremarket && closePrice !== undefined && ethPrice !== undefined) {
    price = ethPrice;
    change = price - closePrice;
    changePercent = change / closePrice * 100;
    timeLabel = profile?.richQuoteData ? getTimeLabels(profile.richQuoteData, session, undefined, translateFn).timestampEth : '-';
  } else if (ethPrice !== undefined) {
    const previousClosePrice = profile?.richQuoteData?.previousClosePrice;
    if (previousClosePrice !== undefined && profile?.richQuoteData) {
      price = ethPrice;
      change = price - previousClosePrice;
      changePercent = change / previousClosePrice * 100;
      timeLabel = getTimeLabels(profile.richQuoteData, session, undefined, translateFn).timestampEth;
    }
  }
  return {
    change,
    changePercent,
    price,
    timeLabel
  };
};
export default QuoteHeaderPriceBox;